export const API_ENDPOINTS = {
  AUTH: {
    // Top-level, not under /api/v1/ — these match the Azure AD / Google
    // OAuth redirect URIs already registered with those providers (see
    // pip-backend/config/urls.py).
    MICROSOFT: "/auth/microsoft",
    GOOGLE: "/auth/google",
    LOGOUT: "/api/v1/auth/logout",
    REFRESH: "/api/v1/auth/refresh",
    ME: "/api/v1/auth/me",
  },
  PROPERTIES: {
    LIST: "/api/v1/properties/",
    OPTIONS: "/api/v1/properties/options/",
    SEARCH: "/api/v1/properties/search/",
    MAP: "/api/v1/properties/map/",
    DASHBOARD_SUMMARY: "/api/v1/properties/dashboard-summary/",
    DETAIL: (id) => `/api/v1/properties/${id}/`,
    NEXT_SR_NO: "/api/v1/properties/next-sr-no/",
    ALLOTMENT_OPTIONS: "/api/v1/properties/allotment-options/",
    RESOLVE_MAP_LINK: "/api/v1/properties/resolve-map-link/",
    AUDIT_LOGS: "/api/v1/properties/audit-logs/",
    AUDIT_LOG_USERS: "/api/v1/properties/audit-logs/users/",
    DOCUMENTS: (propertyId) => `/api/v1/properties/${propertyId}/documents/`,
    DOCUMENTS_ALL: (propertyId) => `/api/v1/properties/${propertyId}/documents/all/`,
    DOCUMENT_DETAIL: (propertyId, docId) => `/api/v1/properties/${propertyId}/documents/${docId}/`,
    MEDIA: (propertyId) => `/api/v1/properties/${propertyId}/media/`,
    MEDIA_DETAIL: (propertyId, mediaId) => `/api/v1/properties/${propertyId}/media/${mediaId}/`,
    MEDIA_REORDER: (propertyId) => `/api/v1/properties/${propertyId}/media/reorder/`,
    SUB_PROPERTIES: (propertyId) => `/api/v1/properties/${propertyId}/sub-properties/`,
    SUB_PROPERTY_DETAIL: (propertyId, subPropertyId) => `/api/v1/properties/${propertyId}/sub-properties/${subPropertyId}/`,
    TENANCIES: (propertyId) => `/api/v1/properties/${propertyId}/tenancies/`,
    TENANCY_DETAIL: (propertyId, tenancyId) => `/api/v1/properties/${propertyId}/tenancies/${tenancyId}/`,
    EXPENSES: (propertyId) => `/api/v1/properties/${propertyId}/expenses/`,
    EXPENSE_DETAIL: (propertyId, expenseId) => `/api/v1/properties/${propertyId}/expenses/${expenseId}/`,
    EXPENSE_CHECK: (propertyId, expenseId, checkType) => `/api/v1/properties/${propertyId}/expenses/${expenseId}/${checkType}/`,
    EXPENSE_CHECK_FILES: (propertyId, expenseId) =>
      `/api/v1/properties/${propertyId}/expenses/${expenseId}/check-files/`,
    EXPENSE_CHECK_FILE_DETAIL: (propertyId, expenseId, fileId) =>
      `/api/v1/properties/${propertyId}/expenses/${expenseId}/check-files/${fileId}/`,
    EXPENSE_QUOTATIONS: (propertyId, expenseId) =>
      `/api/v1/properties/${propertyId}/expenses/${expenseId}/quotations/`,
    EXPENSE_QUOTATION_DETAIL: (propertyId, expenseId, quotationId) =>
      `/api/v1/properties/${propertyId}/expenses/${expenseId}/quotations/${quotationId}/`,
    EXPENSE_QUOTATION_APPROVE: (propertyId, expenseId, quotationId) =>
      `/api/v1/properties/${propertyId}/expenses/${expenseId}/quotations/${quotationId}/approve/`,
    EXPENSE_QUOTATION_FILES: (propertyId, expenseId, quotationId) =>
      `/api/v1/properties/${propertyId}/expenses/${expenseId}/quotations/${quotationId}/files/`,
    EXPENSE_QUOTATION_FILE_DETAIL: (propertyId, expenseId, quotationId, fileId) =>
      `/api/v1/properties/${propertyId}/expenses/${expenseId}/quotations/${quotationId}/files/${fileId}/`,
    BUDGET: (propertyId) => `/api/v1/properties/${propertyId}/budget/`,
    EXPENSE_OPTIONS: "/api/v1/properties/expense-options/",
    EXPENSE_OPTION_DETAIL: (optionId) => `/api/v1/properties/expense-options/${optionId}/`,
    EXPENSE_SUMMARY_BY_PROPERTY: "/api/v1/properties/expense-summary/",
    EXPENSE_FLAG_SUMMARY: (propertyId) => `/api/v1/properties/${propertyId}/expenses/flag-summary/`,
    EXPENSE_RESCAN: (propertyId) => `/api/v1/properties/${propertyId}/expenses/rescan/`,
    EXPENSE_FLAG_DETAIL: (propertyId, expenseId, flagId) =>
      `/api/v1/properties/${propertyId}/expenses/${expenseId}/flags/${flagId}/`,
  },
  CERTIFICATES: {
    LIST: "/api/v1/certificates/",
    STATS: "/api/v1/certificates/stats/",
    OVERVIEW: "/api/v1/certificates/overview/",
    UNREGISTERED: "/api/v1/certificates/unregistered/",
    REQUEST_NEW: "/api/v1/certificates/request-new/",
    AUDIT_EXPORT: "/api/v1/certificates/audit-log/export/",
    SETTINGS: "/api/v1/certificates/settings/",
    BY_VAULT: (vaultId) => `/api/v1/certificates/by-vault/${vaultId}/`,
    DETAIL: (id) => `/api/v1/certificates/${id}/`,
    EVENTS: (id) => `/api/v1/certificates/${id}/events/`,
    EVENT_VOUCHER: (id, eventId) => `/api/v1/certificates/${id}/events/${eventId}/voucher/`,
    COPY: (id) => `/api/v1/certificates/${id}/copy/`,
    COPY_DOWNLOAD: (id) => `/api/v1/certificates/${id}/copy/download/`,
    COPY_CHECK: (id) => `/api/v1/certificates/${id}/copy-check/`,
    COPY_FLAG: (id, flagId) => `/api/v1/certificates/${id}/copy-flags/${flagId}/`,
    // Reused for "delete", "approve_registration" and "reject_registration"
    // too — every direct one-step action shares the same
    // /api/v1/certificates/{id}/{action}/ shape as request/approve/etc.
    ACTION: (id, action) => `/api/v1/certificates/${id}/${action}/`,
    SYNC_TO_PROPERTY: (id) => `/api/v1/certificates/${id}/copy-to-property/`,
  },
  TASKS: {
    LIST: "/api/v1/tasks/",
    DASHBOARD_SUMMARY: "/api/v1/tasks/dashboard-summary/",
    BULK_CREATE: "/api/v1/tasks/bulk/",
    DETAIL: (id) => `/api/v1/tasks/${id}/`,
    BY_PROPERTY: (propertyId) => `/api/v1/properties/${propertyId}/tasks/`,
    DOCUMENTS: (taskId) => `/api/v1/tasks/${taskId}/documents/`,
    DOCUMENT_DETAIL: (taskId, docId) => `/api/v1/tasks/${taskId}/documents/${docId}/`,
    COMMENTS: (taskId) => `/api/v1/tasks/${taskId}/comments/`,
    COMMENT_DETAIL: (taskId, commentId) => `/api/v1/tasks/${taskId}/comments/${commentId}/`,
    BUDGET: (taskId) => `/api/v1/tasks/${taskId}/budget/`,
  },
  ACTIVITIES: {
    LIST: "/api/v1/activities/",
    BY_PROPERTY: (propertyId) => `/api/v1/properties/${propertyId}/activities/`,
    BY_TASK: (taskId) => `/api/v1/tasks/${taskId}/activities/`,
  },
  TEAMS: {
    LIST: "/api/v1/teams/",
    DETAIL: (id) => `/api/v1/teams/${id}/`,
    MEMBERS: (teamId) => `/api/v1/teams/${teamId}/members/`,
    MEMBER_DETAIL: (teamId, userId) =>
      `/api/v1/teams/${teamId}/members/${userId}/`,
  },
  VAULTS: {
    LIST: "/api/v1/vaults/",
    DETAIL: (id) => `/api/v1/vaults/${id}/`,
    QR: (id) => `/api/v1/vaults/${id}/qr/`,
    LABEL: (id) => `/api/v1/vaults/${id}/label/`,
    FLOORS: "/api/v1/vaults/floors/",
    FLOOR_DETAIL: (id) => `/api/v1/vaults/floors/${id}/`,
    ROOMS: "/api/v1/vaults/rooms/",
    ROOM_DETAIL: (id) => `/api/v1/vaults/rooms/${id}/`,
  },
  USERS: {
    LIST: "/api/v1/users/",
    ASSIGNABLE: "/api/v1/users/assignable/",
    DETAIL: (id) => `/api/v1/users/${id}/`,
    RESEND_INVITE: (id) => `/api/v1/users/${id}/resend-invite/`,
    APPROVE: (id) => `/api/v1/users/${id}/approve/`,
    REJECT: (id) => `/api/v1/users/${id}/reject/`,
  },
  GROUPS: {
    LIST: "/api/v1/groups/",
    DETAIL: (id) => `/api/v1/groups/${id}/`,
    PROPERTIES: (id) => `/api/v1/groups/${id}/properties/`,
    PROPERTY_DETAIL: (id, propertyId) => `/api/v1/groups/${id}/properties/${propertyId}/`,
  },
  EXPENSE_IMPORTS: {
    LIST: "/api/v1/admin/expense-imports/",
    DETAIL: (batchId) => `/api/v1/admin/expense-imports/${batchId}/`,
    SHEET_DETAIL: (batchId, sheetId) =>
      `/api/v1/admin/expense-imports/${batchId}/sheets/${sheetId}/`,
    ROWS: (batchId) => `/api/v1/admin/expense-imports/${batchId}/rows/`,
    ROW_DETAIL: (batchId, rowId) => `/api/v1/admin/expense-imports/${batchId}/rows/${rowId}/`,
    VALIDATE: (batchId) => `/api/v1/admin/expense-imports/${batchId}/validate/`,
    RECONCILE: (batchId) => `/api/v1/admin/expense-imports/${batchId}/reconcile/`,
    COMMIT: (batchId) => `/api/v1/admin/expense-imports/${batchId}/commit/`,
    REVERT: (batchId) => `/api/v1/admin/expense-imports/${batchId}/revert/`,
  },
  SMARTSCAN: {
    ROLE_ACCESS: "/api/v1/admin/smartscan-role-access/",
  },
  ASKAI: {
    CONFIG: "/api/v1/askai/config/",
    CONVERSATIONS: "/api/v1/askai/conversations/",
    CONVERSATION_DETAIL: (id) => `/api/v1/askai/conversations/${id}/`,
    MESSAGES: (conversationId) => `/api/v1/askai/conversations/${conversationId}/messages/`,
    MESSAGE_DETAIL: (conversationId, messageId) => `/api/v1/askai/conversations/${conversationId}/messages/${messageId}/`,
    MESSAGE_RETRY: (conversationId, messageId) => `/api/v1/askai/conversations/${conversationId}/messages/${messageId}/retry/`,
    MESSAGE_CANCEL: (conversationId, messageId) => `/api/v1/askai/conversations/${conversationId}/messages/${messageId}/cancel/`,
    ROLE_ACCESS: "/api/v1/admin/askai-role-access/",
  },
};

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  SERVER_ERROR: 500,
};

export const ROLE_OPTIONS = [
  { value: "manager", label: "Manager" },
  { value: "analyst", label: "Analyst" },
  { value: "viewer", label: "Viewer" },
];

export const ADMIN_ROLE_OPTIONS = [
  ...ROLE_OPTIONS,
  { value: "superadmin", label: "SuperAdmin" },
];

export const CESSIE_STATUSES = [
  "auction_process",
  "certificate_process",
  "under_execution",
  "cessie",
];

// Saved when a category is chosen but the sub status is left blank.
export const NO_SUB_STATUS_VALUES = ["cessie", "non_cessie"];

export const AVAILABLE_STATUSES = [
  "available",
  "leased_available",
  "available_partially_leased",
];

export const IN_PROGRESS_STATUSES = [
  "under_construction",
  "under_renovation",
  "under_execution",
  "auction_process",
  "certificate_process",
];

export const PROPERTY_STATUS_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "leased", label: "Leased" },
  { value: "leased_available", label: "Available (Leased)" },
  { value: "available_partially_leased", label: "Available (Partially Leased)" },
  { value: "partially_leased", label: "Partially Leased" },
  { value: "under_construction", label: "Under Construction" },
  { value: "under_renovation", label: "Under Renovation" },
  { value: "under_execution", label: "Under Execution" },
  { value: "auction_process", label: "Auction Process" },
  { value: "certificate_process", label: "Certificate Process" },
  { value: "sold", label: "Sold" },
  { value: "cessie", label: "Cessie (No Sub Status)" },
  { value: "non_cessie", label: "Non-Cessie (No Sub Status)" },
];

export const CESSIE_STATUS_OPTIONS = [
  { value: "auction_process", label: "Auction Process" },
  { value: "certificate_process", label: "Certificate Process" },
  { value: "under_execution", label: "Under Execution" },
];

export const NON_CESSIE_STATUS_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "leased", label: "Leased" },
  { value: "leased_available", label: "Available (Leased)" },
  { value: "available_partially_leased", label: "Available (Partially Leased)" },
  { value: "partially_leased", label: "Partially Leased" },
  { value: "under_construction", label: "Under Construction" },
  { value: "under_renovation", label: "Under Renovation" },
  { value: "sold", label: "Sold" },
];

export const STATUS_TYPE_OPTIONS = [
  { value: "cessie", label: "Cessie" },
  { value: "non_cessie", label: "Non-Cessie" },
];

export const TASK_STATUS_OPTIONS = [
  { value: "todo", label: "To Do" },
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "done", label: "Done" },
  { value: "overdue", label: "Overdue" },
];

// Excludes "overdue" — computed automatically, not user-selectable
export const TASK_STATUS_FORM_OPTIONS = TASK_STATUS_OPTIONS.filter(
  (o) => o.value !== "overdue"
);

export const TASK_PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export const WORK_TYPE_OPTIONS = [
  { value: "property_work", label: "Property Work" },
  { value: "other", label: "Other" },
];

export const OCCUPANCY_STATUS_OPTIONS = [
  { value: "", label: "Unknown" },
  { value: "occupied", label: "Leased" },
  { value: "vacant", label: "Available" },
  { value: "unknown", label: "Unknown" },
];

export const CERTIFICATE_TYPE_OPTIONS = [
  { value: "", label: "Not specified" },
  { value: "SHGB", label: "SHGB" },
  { value: "SHM", label: "SHM" },
  { value: "SHM & HGB", label: "SHM & HGB" },
  { value: "SHSRS", label: "SHSRS" },
  { value: "SHMASRS", label: "SHMASRS" },
  { value: "PPJB", label: "PPJB" },
  { value: "SKT", label: "SKT" },
  { value: "SPTB", label: "SPTB" },
  { value: "SHPTU", label: "SHPTU" },
  { value: "others", label: "Others" },
];

export const RENTAL_PAYMENT_FREQUENCY_OPTIONS = [
  { value: "", label: "Not specified" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

export const PROVINCE_OPTIONS = [
  { value: "aceh", label: "Aceh" },
  { value: "bali", label: "Bali" },
  { value: "banten", label: "Banten" },
  { value: "bengkulu", label: "Bengkulu" },
  { value: "di_yogyakarta", label: "DI Yogyakarta" },
  { value: "dki_jakarta", label: "DKI Jakarta" },
  { value: "gorontalo", label: "Gorontalo" },
  { value: "jambi", label: "Jambi" },
  { value: "jawa_barat", label: "Jawa Barat" },
  { value: "jawa_tengah", label: "Jawa Tengah" },
  { value: "jawa_timur", label: "Jawa Timur" },
  { value: "kepulauan_bangka_belitung", label: "Kepulauan Bangka Belitung" },
  { value: "kepulauan_riau", label: "Kepulauan Riau" },
  { value: "lampung", label: "Lampung" },
  { value: "maluku", label: "Maluku" },
  { value: "maluku_utara", label: "Maluku Utara" },
  { value: "nusa_tenggara_barat", label: "Nusa Tenggara Barat" },
  { value: "nusa_tenggara_timur", label: "Nusa Tenggara Timur" },
  { value: "papua", label: "Papua" },
  { value: "papua_barat", label: "Papua Barat" },
  { value: "papua_barat_daya", label: "Papua Barat Daya" },
  { value: "papua_pegunungan", label: "Papua Pegunungan" },
  { value: "papua_selatan", label: "Papua Selatan" },
  { value: "riau", label: "Riau" },
  { value: "sulawesi_barat", label: "Sulawesi Barat" },
  { value: "sulawesi_selatan", label: "Sulawesi Selatan" },
  { value: "sulawesi_tengah", label: "Sulawesi Tengah" },
  { value: "sulawesi_tenggara", label: "Sulawesi Tenggara" },
  { value: "sulawesi_utara", label: "Sulawesi Utara" },
  { value: "sumatera_barat", label: "Sumatera Barat" },
  { value: "sumatera_selatan", label: "Sumatera Selatan" },
  { value: "sumatera_utara", label: "Sumatera Utara" },
];

export const WRITABLE_PROPERTY_FIELDS = [
  "sr_no",
  "name",
  "location",
  "postal_code",
  "assignees",
  "land_size_m2",
  "building_size_m2",
  "owner_name",
  "ownership_status",
  "certificate_type",
  "certificate_number",
  "certificate_expiry",
  "ex_cessie_bank",
  "underlying",
  "debtor_name",
  "cessie_note",
  "cessie_deed",
  "initial_loan_term",
  "initial_loan_value",
  "total_payment_obligations",
  "physical_condition",
  "nop",
  "njop_per_m2",
  "total_njop",
  "njop_actual",
  "njop_period",
  "pbb_amount",
  "market_value",
  "estimation_market_value",
  "kdb",
  "klb",
  "kdh",
  "ktb",
  "gbs",
  "zoning_info",
  "property_allotment",
  "rental_period",
  "rental_term_start",
  "rental_term_end",
  "total_rental_amount",
  "annual_rental_fee",
  "monthly_rental_fee",
  "rental_paid_frequency",
  "maintenance_expenses",
  "monthly_security_fee",
  "annual_security_fee",
  "construction_cost",
  "estimated_rental",
  "payback_period_months",
  "margin_percentage",
  "status",
  "is_property",
  "occupancy_status",
  "google_maps_link",
  "latitude",
  "longitude",
  "comments",
  "remarks",
  "case_cost",
  "internal_notes",
];

export const WRITABLE_SUB_PROPERTY_FIELDS = [
  "sub_property_name",
  "certificate_type",
  "certificate_number",
  "certificate_expiry",
  "owner_name",
  "land_size_m2",
  "location",
  "postal_code",
  "assignees",
];

export const EXPENSE_ENTRY_STATUS = {
  ACTUAL: "actual",
  TODO: "todo",
  IN_PROGRESS: "in_progress",
};

export const SMARTSCAN_SEVERITY = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
  INFO: "info",
};

export const WRITABLE_TENANCY_FIELDS = [
  "tenant_name",
  "leased_area_spec",
  "rental_term_start",
  "rental_term_end",
  "total_rental_amount",
  "rental_payment_frequency",
  "annual_rental_fee",
  "monthly_rental_fee",
];
